export const widgetPaletteKeys = [
  'primary',
  'header',
  'background',
  'surface',
  'text',
  'muted_text',
  'operator_bubble',
  'visitor_bubble'
] as const
export type WidgetColor = (typeof widgetPaletteKeys)[number]
export type WidgetPalette = Record<WidgetColor, string>
export type WidgetAppearance = { light?: Partial<WidgetPalette>; dark?: Partial<WidgetPalette> }
export type WidgetMode = 'light' | 'dark'

export const defaultWidgetPalettes: Record<WidgetMode, WidgetPalette> = {
  light: {
    primary: '#0d7651',
    header: '#0d7651',
    background: '#ffffff',
    surface: '#ffffff',
    text: '#1e293b',
    muted_text: '#64748b',
    operator_bubble: '#f3f4f6',
    visitor_bubble: '#e2f4ed'
  },
  dark: {
    primary: '#7be99c',
    header: '#0d7651',
    background: '#202c3c',
    surface: '#29384a',
    text: '#edf2f7',
    muted_text: '#b1bfd0',
    operator_bubble: '#334155',
    visitor_bubble: '#2b514b'
  }
}

export const isWidgetColor = (value: unknown): value is string =>
  typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value)

export function normalizeWidgetAppearance(value: unknown): WidgetAppearance {
  const result: WidgetAppearance = { light: {}, dark: {} }
  if (!value || typeof value !== 'object') return result
  for (const mode of ['light', 'dark'] as const) {
    const palette = (value as Record<string, unknown>)[mode]
    if (!palette || typeof palette !== 'object') continue
    for (const key of widgetPaletteKeys) {
      const color = (palette as Record<string, unknown>)[key]
      if (isWidgetColor(color)) result[mode]![key] = color
    }
  }

  return result
}

export function widgetPalette(appearance: WidgetAppearance, mode: WidgetMode): WidgetPalette {
  return { ...defaultWidgetPalettes[mode], ...normalizeWidgetAppearance(appearance)[mode] }
}

export function contrastingText(hex: string): string {
  const channels = [1, 3, 5].map(start => {
    const value = parseInt(hex.slice(start, start + 2), 16) / 255

    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  })
  const luminance = channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722

  return (luminance + 0.05) / 0.05 > 1.05 / (luminance + 0.05) ? '#000000' : '#ffffff'
}

export function widgetColorVariables(appearance: WidgetAppearance, mode: WidgetMode): Record<string, string> {
  const p = normalizeWidgetAppearance(appearance)[mode]!

  return {
    ...(p.primary && {
      '--primary': p.primary,
      '--primary-foreground': contrastingText(p.primary),
      '--ring': p.primary
    }),
    ...(p.header && { '--widget-header': p.header, '--widget-header-foreground': contrastingText(p.header) }),
    ...(p.background && { '--background': p.background }),
    ...(p.text && {
      '--foreground': p.text,
      '--card-foreground': p.text,
      '--popover-foreground': p.text,
      '--secondary-foreground': p.text,
      '--accent-foreground': p.text
    }),
    ...(p.surface && { '--card': p.surface, '--popover': p.surface, '--secondary': p.surface, '--accent': p.surface }),
    ...(p.muted_text && { '--muted-foreground': p.muted_text }),
    ...(p.operator_bubble && {
      '--muted': p.operator_bubble,
      '--widget-operator-bubble': p.operator_bubble,
      '--widget-operator-text': contrastingText(p.operator_bubble)
    }),
    ...(p.visitor_bubble && {
      '--widget-visitor-bubble': p.visitor_bubble,
      '--widget-visitor-text': contrastingText(p.visitor_bubble)
    }),
    ...((p.text || p.background) && {
      '--border': 'color-mix(in srgb, var(--foreground) 18%, var(--background))',
      '--input': 'color-mix(in srgb, var(--foreground) 18%, var(--background))'
    })
  }
}
